import { useEffect } from 'react';
import { useRouteError, isRouteErrorResponse, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Home, RotateCw } from 'lucide-react';
import { Spinner } from '@components/ui';

/**
 * A lazily-imported route chunk failed to load. Happens after a redeploy when a
 * stale tab (or a duplicated/new tab carrying old HTML) requests a hashed chunk
 * whose filename no longer exists. The recovery is a single hard reload to fetch
 * the current HTML + matching hashes — see RouteErrorBoundary.
 */
const CHUNK_ERROR_RE =
  /dynamically imported module|Importing a module script failed|ChunkLoadError|Failed to fetch dynamically/i;
const CHUNK_RELOAD_KEY = 'chunk-reload-ts';
/** Min gap between auto-reloads so a genuinely-missing chunk can't loop forever. */
const CHUNK_RELOAD_WINDOW_MS = 10_000;

/**
 * Route-level error fallback. Mounted via `errorElement` on layout routes so
 * the AppShell (sidebar/header) stays visible while the offending child
 * renders this card. Catches both render errors and loader/action errors.
 */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const { i18n } = useTranslation();
  const isRtl = i18n.dir() === 'rtl';
  const navigate = useNavigate();

  // Auto-recover from stale-chunk failures: reload once (silently) instead of
  // showing the scary error card + forcing the user to click "Retry". A
  // sessionStorage timestamp prevents a reload loop when the chunk is truly
  // gone (then we fall through to the manual card after one attempt).
  const isChunkError = error instanceof Error && CHUNK_ERROR_RE.test(error.message);
  let shouldAutoReload = false;
  if (isChunkError) {
    try {
      const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
      shouldAutoReload = !Number.isFinite(last) || Date.now() - last > CHUNK_RELOAD_WINDOW_MS;
    } catch {
      shouldAutoReload = true;
    }
  }

  useEffect(() => {
    if (!shouldAutoReload) return;
    try {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
    } catch {
      /* private mode / quota — reload anyway */
    }
    window.location.reload();
  }, [shouldAutoReload]);

  if (shouldAutoReload) {
    // Render a spinner (not the error card) while the reload kicks in, so the
    // user never sees the "حدث خطأ" flash for a recoverable stale-chunk error.
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <Spinner />
      </div>
    );
  }

  let title: string;
  let detail: string;
  if (isRouteErrorResponse(error)) {
    title = `${error.status} — ${error.statusText}`;
    detail = typeof error.data === 'string' ? error.data : JSON.stringify(error.data ?? {});
  } else if (error instanceof Error) {
    title = isRtl ? 'حدث خطأ' : 'An error occurred';
    detail = error.message;
  } else {
    title = isRtl ? 'حدث خطأ' : 'An error occurred';
    detail = String(error);
  }

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <div className="max-w-md w-full rounded-lg border border-danger/40 bg-surface p-6 shadow-card">
        <div className="flex items-start gap-3 mb-3">
          <div className="h-10 w-10 rounded-lg grid place-items-center bg-danger/10 text-danger shrink-0">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-fg">{title}</h2>
            <p className="text-xs text-fg-muted mt-1">
              {isRtl ? 'تعذر عرض هذه الصفحة.' : 'This page failed to load.'}
            </p>
          </div>
        </div>
        <pre className="bg-bg-muted text-xs rounded p-3 mb-4 max-h-40 overflow-auto whitespace-pre-wrap" dir="ltr">
          {detail}
        </pre>
        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm hover:bg-bg-muted transition-colors"
          >
            <Home className="h-4 w-4" />
            {isRtl ? 'الرئيسية' : 'Home'}
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand text-white px-3 py-2 text-sm hover:opacity-90 transition-opacity"
          >
            <RotateCw className="h-4 w-4" />
            {isRtl ? 'إعادة المحاولة' : 'Retry'}
          </button>
        </div>
      </div>
    </div>
  );
}
